import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";

export default function MfaCard() {
  const [enabled, setEnabled] = useState(false);
  const [setup, setSetup] = useState(null); // {secret, otpauth_uri}
  const [code, setCode] = useState("");

  const refresh = () => api.get("/auth/mfa/status").then(({ data }) => setEnabled(data.enabled)).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const startSetup = async () => {
    try { const { data } = await api.post("/auth/mfa/setup"); setSetup(data); }
    catch (e) { toast.error("Could not start MFA setup"); }
  };
  const verify = async () => {
    try { await api.post("/auth/mfa/verify", { code }); toast.success("MFA enabled"); setSetup(null); setCode(""); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Invalid code"); }
  };
  const disable = async () => {
    try { await api.post("/auth/mfa/disable", { code }); toast.success("MFA disabled"); setCode(""); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Invalid code"); }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 max-w-2xl" data-testid="mfa-card">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2"><Icons.ShieldCheck className="h-5 w-5 text-blue-600" /><span className="font-medium text-slate-800">Two-Factor Authentication (TOTP)</span></div>
        <Badge className={enabled ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"} data-testid="mfa-status">{enabled ? "Enabled" : "Disabled"}</Badge>
      </div>
      <p className="text-sm text-slate-500 mb-4">Protects high-risk operations such as data purge. Use any authenticator app (Google Authenticator, Authy, 1Password).</p>

      {!enabled && !setup && (
        <Button onClick={startSetup} className="bg-blue-600 hover:bg-blue-700" data-testid="mfa-enable-btn"><Icons.Plus className="h-4 w-4 mr-1.5" /> Enable MFA</Button>
      )}

      {!enabled && setup && (
        <div className="space-y-3">
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
            <div className="text-xs text-slate-500 mb-1">Add this secret to your authenticator app (manual entry):</div>
            <div className="font-mono text-sm text-slate-800 break-all" data-testid="mfa-secret">{setup.secret}</div>
            <div className="text-[11px] text-slate-400 mt-2 break-all">{setup.otpauth_uri}</div>
          </div>
          <div className="space-y-2"><Label>Enter the 6-digit code to confirm</Label><Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" data-testid="mfa-code-input" /></div>
          <Button onClick={verify} className="bg-blue-600 hover:bg-blue-700" data-testid="mfa-verify-btn">Verify & Enable</Button>
        </div>
      )}

      {enabled && (
        <div className="space-y-3">
          <div className="space-y-2"><Label>Enter a current code to disable</Label><Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" data-testid="mfa-code-input" /></div>
          <Button variant="outline" onClick={disable} className="text-rose-600 border-rose-200" data-testid="mfa-disable-btn">Disable MFA</Button>
        </div>
      )}
    </div>
  );
}
